import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Archive, RotateCcw, CheckCircle2, Loader2 } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import UserAvatar from './UserAvatar';
import { TagChipRow } from './TagChip';
import { resolveTags } from './tagPalette';
import './CompletedTasks.css';
import BstId from './BstId';

const PAGE_SIZE = 25;

const TABS = [
  { id: 'all',      label: 'All completed' },
  { id: 'active',   label: 'On the board' },
  { id: 'archived', label: 'Archived' },
];

/**
 * The Completed view (WR-008).
 *
 * Every finished task in the project, whether it is still on the board or has
 * already been archived off it — newest-completed first, paged server-side.
 * On a mature project this runs to thousands of rows, so it is never fetched
 * whole; the board's Done column only ever shows the most recent slice, and
 * this is where the rest lives.
 *
 * A flat list rather than a board: completed work has no workflow left to
 * express.
 *
 * `onRestored` and `onArchived` hand the changed task back to the board so it
 * appears or disappears there without a refetch.
 */
function CompletedTasks({ project, onClose, onRestored, onArchived }) {
  const [tab, setTab]           = useState('all');
  const [tasks, setTasks]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState(null);
  const [busyId, setBusyId]     = useState(null);

  const fetchPage = useCallback(async (state, skip = 0) => {
    skip === 0 ? setLoading(true) : setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
      if (state !== 'all') params.set('state', state);
      const res = await axios.get(`/tasks/project/${project._id}/completed?${params}`);
      const { tasks: page, total: count, archivedTotal: arch, hasMore: more } = res.data;
      setTasks(prev => (skip === 0 ? page : [...prev, ...page]));
      setTotal(count);
      setArchivedTotal(arch);
      setHasMore(more);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load completed tasks.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [project._id]);

  useEffect(() => { fetchPage(tab, 0); }, [fetchPage, tab]);

  // Escape closes, matching the app's other modals.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Restore and archive are mirror images: each moves the task between the
  // board and the archive, and each drops it from the list when the active
  // tab no longer describes it.
  const mutate = async (task, action) => {
    setBusyId(task._id);
    try {
      const res = await axios.put(`/tasks/${task._id}/${action}`);
      const updated = res.data;
      const stillBelongs = tab === 'all'
        || (tab === 'archived' && updated.archived)
        || (tab === 'active' && !updated.archived);

      setTasks(prev => stillBelongs
        ? prev.map(t => (t._id === task._id ? updated : t))
        : prev.filter(t => t._id !== task._id));
      if (!stillBelongs) setTotal(prev => Math.max(0, prev - 1));
      setArchivedTotal(prev => Math.max(0, prev + (action === 'archive' ? 1 : -1)));

      (action === 'archive' ? onArchived : onRestored)?.(updated);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} that task.`);
    } finally {
      setBusyId(null);
    }
  };

  const emptyCopy = {
    all: 'Nothing has been completed in this project yet. Finished tasks collect here, and stay for good.',
    active: 'No completed tasks are on the board right now — they have all been archived.',
    archived: 'Nothing archived yet. Tasks are archived automatically once they have been done for a while, or whenever you archive them by hand. They are never deleted.',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content completed-modal" onClick={e => e.stopPropagation()}>
        <div className="completed-header">
          <div className="completed-title">
            <CheckCircle2 size={20} />
            <div>
              <h2>Completed</h2>
              <p>
                {loading
                  ? 'Loading…'
                  : `${total} completed ${total === 1 ? 'task' : 'tasks'}` +
                    (tab === 'all' && archivedTotal > 0 ? ` · ${archivedTotal} archived` : '')}
              </p>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="completed-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`completed-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="completed-content">
          {error && (
            <div className="completed-error" role="alert">
              <span>{error}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => fetchPage(tab, 0)}>Retry</button>
            </div>
          )}

          {loading ? (
            <div className="completed-skeletons">
              {[0, 1, 2, 3].map(i => <div key={i} className="completed-skeleton" />)}
            </div>
          ) : tasks.length === 0 && !error ? (
            <div className="completed-empty">
              <CheckCircle2 size={32} />
              <h3>Nothing here yet</h3>
              <p>{emptyCopy[tab]}</p>
            </div>
          ) : (
            <>
              <ul className="completed-list">
                {tasks.map(task => (
                  <li key={task._id} className={`completed-row${task.archived ? ' is-archived' : ''}`}>
                    <CheckCircle2 size={16} className="completed-row-icon" />

                    <div className="completed-row-main">
                      <span className="completed-row-title">
                        <BstId id={task.bstId} className="completed-row-bst" />
                        {task.title}
                        {task.archived && <span className="completed-badge">Archived</span>}
                      </span>
                      {/* Archived tasks keep their tag ids, and a rename or
                          recolour reaches them the same way it reaches the
                          board — they resolve against the project (WR-010). */}
                      <TagChipRow tags={resolveTags(project, task.tags)} max={3} />
                      <div className="completed-row-meta">
                        {task.completedAt ? (
                          <span title={format(new Date(task.completedAt), 'PPpp')}>
                            Completed {format(new Date(task.completedAt), 'MMM d, yyyy')}
                          </span>
                        ) : (
                          <span title="Finished before completion times were recorded">
                            Completed date unknown
                          </span>
                        )}
                        {task.archived && task.archivedAt && (
                          <span title={format(new Date(task.archivedAt), 'PPpp')}>
                            Archived {formatDistanceToNow(new Date(task.archivedAt), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                    </div>

                    {task.assignedTo?.length > 0 && (
                      <div className="completed-row-assignees">
                        {task.assignedTo.slice(0, 3).map((u, i) => (
                          <UserAvatar
                            key={u._id}
                            user={u}
                            className="avatar avatar-xs"
                            title={u.name}
                            style={{ width: 24, height: 24, fontSize: 10, marginLeft: i > 0 ? -8 : 0 }}
                          />
                        ))}
                      </div>
                    )}

                    <button
                      className="btn btn-ghost btn-sm completed-action"
                      onClick={() => mutate(task, task.archived ? 'restore' : 'archive')}
                      disabled={busyId === task._id}
                    >
                      {busyId === task._id
                        ? <Loader2 size={14} className="completed-spin" />
                        : task.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                      {task.archived ? 'Restore' : 'Archive now'}
                    </button>
                  </li>
                ))}
              </ul>

              {hasMore && (
                <button
                  className="btn btn-secondary completed-more"
                  onClick={() => fetchPage(tab, tasks.length)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : `Load more (${total - tasks.length} left)`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompletedTasks;
